<script setup lang="ts">
import { reactive, ref } from "vue";
import IconManager, { IconName } from "@/core/IconManager.vue";
import { useI18n } from "vue-i18n";
import { ResourceDetail } from "@/modules/resources/models/resource-detail";
import CustomButton from "@/core/components/CustomButton.vue";
import { ResourceType } from "@/modules/resources/models/resource-type";

export interface ResourceAction {
  icon: IconName;
  title?: string;
  onClick: (resource: ResourceDetail) => void;
  colorClass?: string;
  isLoading?: boolean;
}

defineProps<{
  data: ResourceType;
  actions?: ResourceAction[];
}>();

const isOpen = ref(false);
const expandedIds = ref(new Set<string>());
const { t } = useI18n();
const failedImageIds = reactive(new Set<string>());

function toggle(id: string) {
  if (expandedIds.value.has(id)) {
    expandedIds.value.delete(id);
  } else {
    expandedIds.value.add(id);
  }
  expandedIds.value = new Set(expandedIds.value);
}

function isExpanded(id: string) {
  return expandedIds.value.has(id);
}

function getResourceImageUrl(resource: ResourceDetail, width: number): string {
  return `${import.meta.env.VITE_API_URL}/Resources/${resource.resourceId}/image?w=${width}&v=${resource.imageVersion}`;
}
</script>

<template>
  <div class="flex flex-col bg-white px-5 py-4 shadow-md rounded-xl gap-4">
    <!-- Root -->
    <div
      class="flex flex-row justify-between items-center"
      @click="isOpen = !isOpen"
    >
      <div class="flex flex-row items-center gap-2">
        <IconManager
          name="ArrowDown"
          class="text-primary transition-transform duration-200"
          :class="isOpen ? 'rotate-0' : '-rotate-90'"
        />
        <span class="font-extrabold">{{ data.name }}</span>
      </div>
      <div
        class="rounded-xl bg-background font-bold text-light-gray px-2 py-0.5 text-xs"
      >
        {{ data.quantity }} {{ t("resources.list.resources") }}
      </div>
    </div>

    <template v-if="isOpen">
      <div class="flex flex-col gap-2">
        <div v-for="subType in data.subTypes" :key="subType.name" class="pl-2">
          <!-- SubType level -->
          <div
            class="flex flex-row justify-between items-center gap-2 bg-[#F5F7FA] rounded-t-lg px-3 py-2.5"
            @click="toggle(`sub-${data.id}-${subType.id}`)"
          >
            <div class="flex flex-row items-center gap-2">
              <IconManager
                name="ArrowDown"
                class="text-primary transition-transform duration-200"
                :class="
                  isExpanded(`sub-${data.id}-${subType.id}`)
                    ? 'rotate-0'
                    : '-rotate-90'
                "
              />
              <span class="font-bold">
                {{ subType.name }}
              </span>
            </div>
            <div
              class="rounded-xl bg-background font-bold text-light-gray px-2 py-0.5 text-xs"
            >
              {{ subType.quantity }} {{ t("resources.list.resources") }}
            </div>
          </div>

          <div
            v-if="isExpanded(`sub-${data.id}-${subType.id}`)"
            class="bg-[#F5F7FA] p-2.5"
          >
            <table class="w-full border-collapse">
              <thead>
                <tr class="text-light-gray font-bold text-center">
                  <th class="column-header"></th>
                  <th class="column-header">
                    {{ t("resources.list.table.resource") }}
                  </th>
                  <th class="column-header">
                    {{ t("resources.list.table.type") }}
                  </th>
                  <th class="column-header">
                    {{ t("resources.list.table.quantity") }}
                  </th>
                  <th class="column-header">
                    {{ t("resources.list.table.avail") }}
                  </th>
                  <th class="column-header">
                    {{ t("resources.list.table.dissam") }}
                  </th>
                  <th class="column-header">
                    {{ t("resources.list.table.cost") }}
                  </th>
                  <th class="column-header">{{ t("common.table.actions") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="resource in subType.resources"
                  :key="resource.resourceId"
                  class="bg-white text-center"
                >
                  <td class="column-content">
                    <div class="flex flex-row gap-4 items-center w-fit">
                      <img
                        v-if="
                          resource.imageVersion &&
                          !failedImageIds.has(resource.resourceId)
                        "
                        :src="getResourceImageUrl(resource, 64)"
                        :alt="resource.name"
                        class="size-12 rounded-lg object-cover shrink-0"
                        @error="failedImageIds.add(resource.resourceId)"
                      />
                      <IconManager
                        v-else
                        name="Image"
                        class="size-12 text-light-gray shrink-0"
                      />
                    </div>
                  </td>
                  <td class="column-content">
                    {{ resource.name }}
                  </td>
                  <td class="column-content">{{ resource.type }}</td>
                  <td class="column-content">
                    {{ resource.quantity ?? resource.maxQuantity }}
                  </td>
                  <td class="column-content">
                    {{ resource.assemblyTime }}
                  </td>
                  <td class="column-content">
                    {{ resource.disassemblyTime }}
                  </td>
                  <td class="column-content">{{ resource.cost }}</td>
                  <td>
                    <div
                      class="flex flex-row gap-2 items-center justify-center"
                    >
                      <CustomButton
                        v-for="action in actions"
                        @click="action.onClick(resource)"
                        :title="action.title"
                        :button-styles="action.colorClass"
                        :is-loading="action.isLoading"
                      >
                        <IconManager :name="action.icon"
                      /></CustomButton>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.column-header {
  padding: 12px 10px;
}
.column-content {
  padding: 12px 10px;
}
</style>
